import { useState } from "react";
import {
  ChartConfig,
  ChartContainer,
  ChartStyle,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import {
  CartesianGrid,
  Label as ChartLabel,
  Pie,
  PieChart,
  XAxis,
  YAxis,
  Line,
  LineChart,
} from "recharts";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  MoreHorizontal,
  Download,
  Send,
  Loader2,
  ArrowUpDown,
} from "lucide-react";
import { format } from "date-fns";
import { TZDate } from "@date-fns/tz";
import { es } from "date-fns/locale";
import {
  useAllReports,
  useReportFile,
  useShareReport,
  useUserDetails,
} from "@/hooks";
import { AgentBadge } from "@/components/agent-badge";
import { Report } from "@/api/get-all-reports";
import { Link, Navigate } from "react-router";

const ReportsPage = () => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState<string | null>(null);
  const [recipientEmail, setRecipientEmail] = useState("");

  const { data, isLoading, isError } = useAllReports();
  const sendReport = useShareReport();
  const reportFile = useReportFile();
  const { data: user } = useUserDetails();

  const openSendDialog = (reportId: string) => {
    setSelectedReport(reportId);
    setSendDialogOpen(true);
  };

  const columns: ColumnDef<Report>[] = [
    {
      id: "id",
      accessorFn: (row) => (
        <div className="grid gap-0 font-mono">
          <p>{row?.projectId ?? row?.optionalId ?? "---"}</p>
          <p className="text-xs text-muted-foreground">{row.id}</p>
        </div>
      ),
      header: () => <div className="font-mono">ID</div>,
      cell: (info) => info.getValue(),
    },
    {
      accessorKey: "generatedAt",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            className="hover:bg-accent/10 focus:bg-accent/10 gap-1"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            <p>Creado</p>
            <ArrowUpDown className="size-4" />
          </Button>
        );
      },
      cell: ({ row }) => {
        const date = new TZDate(row.getValue("generatedAt") + "Z");
        return (
          <div className="grid gap-0">
            {format(date, "PPP", { locale: es })}
            <span className="text-xs text-muted-foreground">
              {format(date, "hh:mm aaa z", { locale: es })}
            </span>
          </div>
        );
      },
      sortingFn: "datetime",
    },
    {
      accessorKey: "agent",
      header: "Agente",
      cell: ({ row }) => {
        return <AgentBadge variant={row.getValue("agent")} />;
      },
    },
    {
      accessorKey: "shareCount",
      header: "Compartido",
      cell: ({ row }) => {
        return (
          <p className="flex items-center gap-1">
            <span>{row.getValue("shareCount")}</span>
          </p>
        );
      },
    },
    {
      id: "actions",
      header: "Acciones",
      cell: ({ row }) => {
        const report = row.original;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Abrir menú</span>
                <MoreHorizontal className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                disabled={!report.inputPath}
                onClick={async () => {
                  const url = await reportFile.mutateAsync({
                    reportId: report.id,
                    filename: report.inputPath ?? "",
                  });
                  window.open(url, "_blank");
                }}
              >
                <Download className="size-4" /> Datos Excel
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={!report.optionsPathJson}
                onClick={async () => {
                  const url = await reportFile.mutateAsync({
                    reportId: report.id,
                    filename: report.optionsPathJson ?? "",
                  });
                  window.open(url, "_blank");
                }}
              >
                <Download className="size-4" /> Opciones Json
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={!report.outputPathPdf}
                onClick={async () => {
                  const url = await reportFile.mutateAsync({
                    reportId: report.id,
                    filename: report.outputPathPdf ?? "",
                  });
                  window.open(url, "_blank");
                }}
              >
                <Download className="size-4" /> Reporte Pdf
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => openSendDialog(report.id)}
                className="gap-1"
                asChild
              >
                <Link to={`/reportes/${report.id}`}>
                  <Send className="size-4" /> Compartir reporte
                </Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const table = useReactTable<Report>({
    data: data ?? [],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    columns: columns as ColumnDef<Report, any>[],
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      sorting,
      columnFilters,
      pagination,
    },
    manualPagination: true,
    pageCount: Math.ceil((data?.length || 0) / pagination.pageSize),
  });

  const chartConfig = {
    reports: {
      label: "Reportes",
      color: "hsl(var(--muted))",
    },
    lighting: {
      label: "Iluminación",
      color: "hsl(var(--chart-1))",
    },
    temperature: {
      label: "TGBH",
      color: "hsl(var(--chart-2))",
    },
    noiseDose: {
      label: "Ruido Dosis",
      color: "hsl(var(--chart-3))",
    },
  } satisfies ChartConfig;

  const chartData =
    isLoading || data?.length === 0
      ? []
      : [
          {
            generated: data?.filter((report) => report.agent === "lighting")
              .length,
            agent: "Iluminación",
            fill: "var(--chart-1)",
          },
          {
            generated: data?.filter((report) => report.agent === "temperature")
              .length,
            agent: "TGBH",
            fill: "var(--chart-2)",
          },
          {
            generated: data?.filter((report) => report.agent === "noise_dose")
              .length,
            agent: "Ruido Dosis",
            fill: "var(--chart-3)",
          },
        ];

  const defaultChartHistoryData = Array.from({ length: 30 }, (_, i) => ({
    month: format(new TZDate(new Date().getFullYear(), new Date().getMonth(), new Date().getDate() - (30-i-1)), "dd/MMM", { locale: es }),
    lighting: 0,
    temperature: 0,
    noiseDose: 0,
    total: 0,
  }));

  const chartHistoryData = (isLoading || data?.length === 0)
    ? defaultChartHistoryData
    : defaultChartHistoryData.map((item) => {
        const reports = data?.filter((report) => {
          const date = new TZDate(report.generatedAt + "Z");
          return format(date, "dd/MMM", { locale: es }) === item.month;
        });
        return reports?.reduce((acc, report) => { 
           if (report.agent === "lighting") {
            acc.lighting += 1;
           } else if (report.agent === "temperature") {
            acc.temperature += 1;
           } else if (report.agent === "noise_dose") {
            acc.noiseDose += 1;
           }
           acc.total += 1;
           return acc;
        }, {month: item.month, lighting: 0, temperature: 0, noiseDose: 0, total: 0});
      })

  const maxY = Math.max(...chartHistoryData.map((item) => Math.max(item?.lighting ?? 0, item?.noiseDose ?? 0, item?.temperature ?? 0))) + 1;


  if (user?.type === "anonymous") {
    return <Navigate to="/inicia-sesion?redirect=reportes" />;
  }

  return (
    <div className="grid gap-3 w-full">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-lg font-semibold">Reportes</h1>
          <p className="text-muted-foreground text-xs">
            Visualiza y administra todos tus reportes generados
          </p>
        </div>
        {/* <div className="flex items-center gap-4">
          <Input
            placeholder="Filtrar por ID..."
            value={(table.getColumn("id")?.getFilterValue() as string) ?? ""}
            onChange={(event) =>
              table.getColumn("id")?.setFilterValue(event.target.value)
            }
            className="max-w-sm"
          />
        </div> */}
      </div>

      <Separator />
      <div className="grid gap-3 items-center lg:flex">
        <Card data-chart="reports-pie" className="flex w-min">
          <ChartStyle id="reports-pie" config={chartConfig} />
          <CardHeader className="grid gap-1">
            <CardTitle>Total</CardTitle>
            <CardDescription>Reportes generados</CardDescription>
          </CardHeader>
          <CardContent>
            <ChartContainer
              id="reports-pie"
              config={chartConfig}
              className="aspect-square w-[200px]"
            >
              <PieChart>
                <ChartTooltip
                  cursor={false}
                  content={<ChartTooltipContent hideLabel />}
                />
                <Pie
                  data={chartData}
                  dataKey="generated"
                  nameKey="agent"
                  innerRadius={60}
                  strokeWidth={5}
                >
                  <ChartLabel
                    content={({ viewBox }) => {
                      if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                        return (
                          <text
                            x={viewBox.cx}
                            y={viewBox.cy}
                            textAnchor="middle"
                            dominantBaseline="middle"
                          >
                            <tspan
                              x={viewBox.cx}
                              y={viewBox.cy}
                              className="fill-foreground text-3xl font-bold"
                            >
                              {chartData
                                .reduce(
                                  (acc, item) => acc + (item.generated ?? 0),
                                  0
                                )
                                .toLocaleString()}
                            </tspan>
                            <tspan
                              x={viewBox.cx}
                              y={(viewBox.cy || 0) + 24}
                              className="fill-muted-foreground"
                            >
                              Reportes
                            </tspan>
                          </text>
                        );
                      }
                    }}
                  />
                </Pie>
              </PieChart>
            </ChartContainer>
          </CardContent>
        </Card>
        <Card className="w-full">
          <CardHeader>
            <CardTitle>Serie</CardTitle>
            <CardDescription>
              Reportes generados este mes
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ChartContainer config={chartConfig} className="w-full h-[200px]">
              <LineChart
                data={chartHistoryData}
                margin={{
                  left: 12,
                  right: 12,
                }}
              >
                <CartesianGrid vertical={true} />
                <XAxis
                  dataKey="month"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  tickFormatter={(value) => value.slice(0, 6)}
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  domain={[0, maxY]}
                  tickFormatter={(value) => value.toLocaleString()}
                />
                <ChartTooltip
                  cursor={false}
                  content={<ChartTooltipContent indicator="line" />}
                />
                <Line
                  dataKey="lighting"
                  type="monotone"
                  stroke="var(--chart-1)"
                  dot={false}
                />
                <Line
                  dataKey="temperature"
                  type="monotone"
                  stroke="var(--chart-2)"
                  dot={false}
                />
                <Line
                  dataKey="noiseDose"
                  type="monotone"
                  stroke="var(--chart-3)"
                  dot={false}
                />
              </LineChart>
            </ChartContainer>
          </CardContent>
        </Card>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="text-center py-10"
                >
                  <div className="flex justify-center items-center">
                    <Loader2 className="h-6 w-6 animate-spin mr-2" />
                    <span>Cargando reportes...</span>
                  </div>
                </TableCell>
              </TableRow>
            ) : isError ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="text-center py-10 text-red-500"
                >
                  Error al cargar los reportes. Por favor, intenta de nuevo.
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="text-center py-10"
                >
                  No hay reportes disponibles.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between space-x-2 py-4">
        <div className="flex items-center space-x-2">
          <p className="text-sm text-muted-foreground">
            Página {table.getState().pagination.pageIndex + 1} de{" "}
            {table.getPageCount() || 1}
          </p>
          <Select
            value={table.getState().pagination.pageSize.toString()}
            onValueChange={(value) => {
              table.setPageSize(Number(value));
            }}
          >
            <SelectTrigger className="h-8 w-[70px]">
              <SelectValue placeholder={table.getState().pagination.pageSize} />
            </SelectTrigger>
            <SelectContent side="top">
              {[10, 20, 30, 40, 50].map((pageSize) => (
                <SelectItem key={pageSize} value={`${pageSize}`}>
                  {pageSize}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-sm text-muted-foreground">
            de {data?.length || 0} reportes
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            Anterior
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            Siguiente
          </Button>
        </div>
      </div>

      <Dialog open={sendDialogOpen} onOpenChange={setSendDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Enviar reporte</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="email" className="text-right">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="correo@ejemplo.com"
                className="col-span-3"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="submit"
              onClick={async () => {
                await sendReport.mutateAsync({
                  reportId: selectedReport!,
                  email: recipientEmail,
                });
                setSendDialogOpen(false);
                setRecipientEmail("");
                setSelectedReport(null);
              }}
              disabled={
                sendReport.isPending || !recipientEmail || !selectedReport
              }
            >
              {sendReport.isPending ? (
                <>
                  <Loader2 className="mr-2 size-4 animate-spin" />
                  Enviando...
                </>
              ) : (
                "Enviar"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReportsPage;
